/**
 * Context Switcher Component
 *
 * Allows users with multiple contexts to switch between them
 * (e.g. personal, school, business, platform).
 */

import { FiUser, FiBookOpen, FiBriefcase, FiShield } from 'react-icons/fi'
import { useAuthStore, selectAvailableContexts, selectActiveContext } from '../../store'
import type { ContextType } from '../../types/context.types'
import { ContextDisplayNames } from '../../types/context.types'
import { cn } from '../../lib/utils'

const contextIcons: Record<ContextType, React.ReactNode> = {
  personal: <FiUser size={14} />,
  school: <FiBookOpen size={14} />,
  business: <FiBriefcase size={14} />,
  platform: <FiShield size={14} />,
}

export function ContextSwitcher() {
  const availableContexts = useAuthStore(selectAvailableContexts)
  const activeContext = useAuthStore(selectActiveContext)
  const switchContext = useAuthStore((state) => state.switchContext)

  // Only show if user has more than one context
  if (availableContexts.length <= 1) {
    return null
  }

  return (
    <div className="p-2 bg-background-secondary rounded-[var(--radius-button)] mb-4">
      <p className="text-xs text-foreground-muted mb-2 px-2">
        Context
      </p>
      <div className="flex gap-2">
        {availableContexts.map((ctx) => (
          <ContextButton
            key={ctx}
            icon={contextIcons[ctx]}
            label={ContextDisplayNames[ctx]}
            isActive={activeContext === ctx}
            onClick={() => switchContext(ctx)}
          />
        ))}
      </div>
    </div>
  )
}

interface ContextButtonProps {
  icon: React.ReactNode
  label: string
  isActive: boolean
  onClick: () => void
}

function ContextButton({ icon, label, isActive, onClick }: ContextButtonProps) {
  return (
    <button
      className={cn(
        'flex-1 inline-flex items-center justify-center gap-1 rounded-[var(--radius-button)] px-3 h-9 text-xs transition-colors cursor-pointer',
        isActive
          ? 'bg-primary text-white hover:bg-primary-hover'
          : 'bg-transparent text-foreground-muted hover:bg-background'
      )}
      onClick={onClick}
    >
      {icon}
      <span className="text-xs">{label}</span>
    </button>
  )
}
